import { useCallback, useRef, useState } from "react";

export interface SwipeState {
  readonly swiping: boolean;
  readonly swipeX: number;
}

export interface SwipeHandlers {
  readonly onTouchStart: (e: React.TouchEvent) => void;
  readonly onTouchMove: (e: React.TouchEvent) => void;
  readonly onTouchEnd: () => void;
}

interface UseSwipeOptions {
  readonly onSwipeLeft?: () => void;
  readonly onSwipeRight?: () => void;
  readonly threshold?: number;
}

export function useSwipe(options: UseSwipeOptions): SwipeState & SwipeHandlers {
  const { onSwipeLeft, onSwipeRight, threshold = 80 } = options;
  const startX = useRef(0);
  const startY = useRef(0);
  const isTracking = useRef(false);
  const [swiping, setSwiping] = useState(false);
  const [swipeX, setSwipeX] = useState(0);

  const onTouchStart = useCallback((e: React.TouchEvent) => {
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    isTracking.current = true;
    setSwiping(false);
    setSwipeX(0);
  }, []);

  const onTouchMove = useCallback(
    (e: React.TouchEvent) => {
      if (!isTracking.current) return;
      const dx = e.touches[0].clientX - startX.current;
      const dy = e.touches[0].clientY - startY.current;
      if (Math.abs(dy) > Math.abs(dx) && !swiping) {
        isTracking.current = false;
        return;
      }
      if (Math.abs(dx) > 10) {
        setSwiping(true);
        setSwipeX(dx);
      }
    },
    [swiping],
  );

  const onTouchEnd = useCallback(() => {
    if (swipeX < -threshold && onSwipeLeft) {
      onSwipeLeft();
    } else if (swipeX > threshold && onSwipeRight) {
      onSwipeRight();
    }
    isTracking.current = false;
    setSwiping(false);
    setSwipeX(0);
  }, [swipeX, threshold, onSwipeLeft, onSwipeRight]);

  return { swiping, swipeX, onTouchStart, onTouchMove, onTouchEnd };
}
